import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import s from './words.module.css';
import { getData } from '../middleware/apiMiddleware';
import { useNavigate } from "react-router-dom";
import VocabAdvDetail from "../components/VocabAdvDetail";
import VocabBatchProcess from "../components/VocabBatchProcess";

export default function Words() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Mode toggle state: false = List Mode, true = Batch Mode
    const [isBatchMode, setIsBatchMode] = useState(false);

    // Default state: 500 items per page
    const [words, setWords] = useState([]);
    const [pagingInfo, setPagingInfo] = useState({
        page: 1,
        limit: 500,
        totalItems: 0,
        totalPages: 1
    });
    const [loading, setLoading] = useState(true);
    const [error] = useState(null);
    const [chosenWord, setChosenWord] = useState(null);
    const [searchTerm, setSearchTerm] = useState(searchParams.get('word') || '');

    // Toggle between list and batch mode
    const toggleViewMode = () => {
        setIsBatchMode(prev => !prev);
    };

    // 🔥 NEW: Click handler to set selected word
    const handleWordClick = (word) => {
        setChosenWord(word);
    };

    // 🔥 HELPER FUNCTION: Split array into 4 BALANCED COLUMNS (matches your exact math)
    const splitIntoFourColumns = (arr) => {
        const total = arr.length;
        const cols = 4;
        const baseSize = Math.floor(total / cols);
        let remainder = total % cols;

        const col1 = arr.slice(0, baseSize + (remainder > 0 ? 1 : 0));
        remainder--;
        const col2 = arr.slice(col1.length, col1.length + baseSize + (remainder > 0 ? 1 : 0));
        remainder--;
        const col3 = arr.slice(col1.length + col2.length, col1.length + col2.length + baseSize + (remainder > 0 ? 1 : 0));
        const col4 = arr.slice(col1.length + col2.length + col3.length);

        return [col1, col2, col3, col4];
    };

    // Split words into 4 columns for rendering
    const [col1, col2, col3, col4] = splitIntoFourColumns(words);

    // Fetch data effect
    useEffect(() => {
        const page = searchParams.get('page') || '1';
        const limit = searchParams.get('limit') || '50';
        const searchWord = searchParams.get('word') || '';

        setSearchTerm(searchWord);

        const params = new URLSearchParams({ 
            page, 
            limit,
            ...(searchWord && { word: searchWord })
        });
        const apiUrl = `vocabApi/listVocabAdv?${params.toString()}`;

        setLoading(true);

        getData(apiUrl, navigate)
            .then(res => {
                setWords(res.data || []);
                setPagingInfo(res.pagination || {
                    page: 1,
                    limit: 50,
                    totalItems: 0,
                    totalPages: 1
                });
            })
            .catch(err => {
                console.error("Failed to fetch vocabulary:", err);
            })
            .finally(() => {
                setLoading(false);
            });

    }, [searchParams, navigate]);

    // Event Handlers
    const handlePageChange = (newPage) => {
        setSearchParams(prev => {
            prev.set('page', newPage.toString());
            return prev;
        }, { replace: true });
    };

    const handleLimitChange = (event) => {
        const newLimit = event.target.value;
        setSearchParams(prev => {
            prev.set('limit', newLimit);
            prev.set('page', '1');
            return prev;
        }, { replace: true });
    };

    const handleSearch = () => {
        setSearchParams(prev => {
            if (searchTerm) {
                prev.set('word', searchTerm);
            } else {
                prev.delete('word');
            }
            prev.set('page', '1');
            return prev;
        }, { replace: true });
    };
    
    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') handleSearch();
    };

    // Pagination Helpers
    const currentPage = pagingInfo.page;
    const totalPages = pagingInfo.totalPages;
    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage >= totalPages;

    return (
        <div className={s.container}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                <h1>Advanced Vocabulary</h1>
                {/* Mode Toggle Button */}
                <button 
                    onClick={toggleViewMode}
                    className={s.searchButton}
                >
                    {isBatchMode ? 'Switch to List View' : 'Switch to Batch Process'}
                </button>
            </div>

            {/* Only show search bar in List Mode */}
            {!isBatchMode && (
                <div className={s.searchContainer}>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleSearchKeyPress}
                        placeholder="Search starting word (e.g., a, ab, accept)"
                        className={s.searchInput}
                    />
                    <button onClick={handleSearch} className={s.searchButton}>
                        Search
                    </button>
                </div>
            )}

            {/* Loading State */}
            {loading && <div className={s.loading}>Loading vocabulary...</div>}

            {/* Conditional Render: List Mode / Batch Mode */}
            {!loading && (
                <>
                    {!isBatchMode ? (
                        // Original 4-column List View
                        <>
                            <div className={s.fourColumnContainer}>
                                {words.length === 0 ? (
                                    <p className={s.empty}>No vocabulary words found</p>
                                ) : (
                                    <>
                                        {/* Column 1 (Left Most - Filled First) */}
                                        <div className={s.wordColumn}>
                                            {col1.map((item, index) => (
                                                <div key={index} className={s.wordItem} onClick={() => handleWordClick(item.word)}>
                                                    {item.word}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Column 2 */}
                                        <div className={s.wordColumn}>
                                            {col2.map((item, index) => (
                                                <div key={index} className={s.wordItem} onClick={() => handleWordClick(item.word)}>
                                                    {item.word}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Column 3 */}
                                        <div className={s.wordColumn}>
                                            {col3.map((item, index) => (
                                                <div key={index} className={s.wordItem} onClick={() => handleWordClick(item.word)}>
                                                    {item.word}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Column 4 (Right Most) */}
                                        <div className={s.wordColumn}>
                                            {col4.map((item, index) => (
                                                <div key={index} className={s.wordItem} onClick={() => handleWordClick(item.word)}>
                                                    {item.word}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Pagination */}
                            <div className={s.paginationContainer}>
                                <div className={s.limitSelector}>
                                    <span>Items per page:</span>
                                    <select
                                        value={pagingInfo.limit}
                                        onChange={handleLimitChange}
                                        className={s.limitSelect}
                                    >
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>

                                <div className={s.pageButtons}>
                                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={isFirstPage} className={s.pageBtn}>Previous</button>
                                    <span className={s.pageInfo}>Page {currentPage} of {totalPages}</span>
                                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={isLastPage} className={s.pageBtn}>Next</button>
                                </div>
                            </div>
                        </>
                    ) : (
                        // Batch Process Mode
                        <VocabBatchProcess />
                    )}
                </>
            )}

            {/* Detail component renders when a word is clicked (only valid in list mode) */}
            {!isBatchMode && chosenWord && <VocabAdvDetail word={chosenWord} />}

        </div>
    );
}